import {safeGet} from "./utils";
import {readWindowProperties} from "./property-reader";
import {
  detectAutomationHint,
  getAncestorOriginCount,
  isCanvasSupported,
  isInIframe,
  isWebGlSupported,
} from "./detectors";
import {
  readMediaPreferences,
  readUserAgentData,
  resolveDocumentVisibility,
  resolveLocale,
} from "./environment";

export function collectBrowserInformation(minimalDataMode: boolean): { [key: string]: any } {
  const result = readWindowProperties(minimalDataMode);

  // Always collected — sent in both modes. `plugins` and the dom-* probes were already
  // shipped in minimal mode prior to widget 3.0; keeping them preserves backward-compatible
  // behaviour. `intl-locale`, `embedding-*`, `document-visibilityState` are new but anonymous.
  Object.assign(result, {
    'dom-automationNote': safeGet(detectAutomationHint),
    'dom-webGlSupport': safeGet(isWebGlSupported),
    'dom-canvasSupport': safeGet(isCanvasSupported),
    'plugins': safeGet(() => Array.from(navigator.plugins).map(p => p.name).join(',')),
    'intl-locale': safeGet(resolveLocale),
    'embedding-isInIframe': safeGet(isInIframe),
    'embedding-ancestorOriginCount': safeGet(getAncestorOriginCount),
    'document-visibilityState': safeGet(resolveDocumentVisibility),
  });

  // Standard mode only — new identifying data added in widget 3.0: Client Hints (granular
  // UA, more specific than navigator.userAgent) and media preferences (lifestyle profile).
  if (!minimalDataMode) {
    const userAgentData = safeGet(readUserAgentData);
    Object.assign(result, {
      'window-navigator-userAgentData-brands': userAgentData?.brands ?? null,
      'window-navigator-userAgentData-mobile': userAgentData?.mobile ?? null,
      'window-navigator-userAgentData-platform': userAgentData?.platform ?? null,
    });

    const mediaPreferences = safeGet(readMediaPreferences);
    Object.assign(result, {
      'media-prefersColorScheme': mediaPreferences?.prefersColorScheme ?? null,
      'media-prefersReducedMotion': mediaPreferences?.prefersReducedMotion ?? null,
      'media-prefersContrast': mediaPreferences?.prefersContrast ?? null,
      'media-forcedColors': mediaPreferences?.forcedColors ?? null,
    });
  }

  return result;
}
